// Simulacros del Plan de Emergencias v2 (PE-B2): lectura y escritura en Supabase (M38).
//  - emergency_drills: programar (crea el evento del Calendario SSO con el recordatorio de siempre), registrar el
//    resultado, firmar el informe (PDF y firmas en <empresa>/simulacros/<simulacro>/informe/, carpeta congelada) y
//    reabrir (RPC reabrir_simulacro, solo admin, con motivo; queda en audit_logs).
//  - emergency_drill_participants: brigadistas presentes (opcional por trabajador).
//  - emergency_drill_evidence: fotos o PDF en <empresa>/simulacros/<simulacro>/evidencias/ (mismas reglas de tipo,
//    cabecera y tamaño que las evidencias de las acciones; URL firmada de 120 s).
//  - emergency_drill_annual_reports: informe anual firmado, inmutable, en <empresa>/simulacros/anual/.

import { supabase } from '../../supabaseClient';
import { mensajeErrorPE, MENSAJE_SIN_PERMISO } from './pe-datos';
import { nombreSeguro, revisarEvidencia, SEGUNDOS_URL_EVIDENCIA, TipoEvidencia } from './pe-evidencias';
import { BUCKET_PLAN } from './pe-versiones';
import type { FilaSimulacro, FirmaInforme, MetodoFirma, RolFirma } from './pe-simulacros';
import { ETIQUETA_TIPO, fechaEc } from './pe-simulacros';

const falla = (error: { code?: string; message?: string } | null, accion: string): never => {
  throw new Error(mensajeErrorPE(error, accion));
};

const num = (v: unknown): number | null => (v === null || v === undefined || v === '' ? null : Number(v));

export function mapSimulacro(r: any): FilaSimulacro {
  return {
    ...r,
    scope_all: r.scope_all ?? true, scope_sector_ids: r.scope_sector_ids ?? [], report_signatures: r.report_signatures ?? [],
    reopen_history: r.reopen_history ?? [], participants_count: num(r.participants_count), expected_occupants: num(r.expected_occupants),
    assembly_count: num(r.assembly_count),
  } as FilaSimulacro;
}

export async function cargarSimulacros(companyId: string): Promise<FilaSimulacro[]> {
  const { data, error } = await supabase.from('emergency_drills').select('*').eq('company_id', companyId).order('drill_date', { ascending: false });
  if (error) falla(error, 'leer los simulacros');
  return (data ?? []).map(mapSimulacro);
}

// Campos que escribe el formulario (lo demás lo pone la base o el flujo de firma).
export type SimulacroEditable = Pick<FilaSimulacro,
  'status' | 'scheduled_date' | 'performed_date' | 'start_time' | 'drill_type' | 'drill_type_other' | 'objective' | 'scenario' | 'threat_key'
  | 'location' | 'scope_all' | 'scope_sector_ids' | 'announced' | 'coordinator_worker_id' | 'coordinator_role' | 'observers'
  | 'participants_count' | 'expected_occupants' | 'assembly_count' | 'alarm_time' | 'evacuation_start_time' | 'last_arrival_time'
  | 'incidents_notes' | 'lessons_learned' | 'conclusion'> & { id?: string };

const limpio = (s: string | null | undefined) => (s && s.trim() ? s.trim() : null);

function filaParaGuardar(s: SimulacroEditable) {
  return {
    status: s.status, scheduled_date: s.scheduled_date || null, performed_date: s.performed_date || null, start_time: s.start_time || null,
    drill_date: s.performed_date || s.scheduled_date, drill_type: s.drill_type, drill_type_other: s.drill_type === 'otro' ? limpio(s.drill_type_other) : null,
    objective: limpio(s.objective), scenario: limpio(s.scenario), threat_key: s.threat_key || null, location: limpio(s.location),
    scope_all: s.scope_all, scope_sector_ids: s.scope_all ? [] : s.scope_sector_ids, announced: s.announced,
    coordinator_worker_id: s.coordinator_worker_id || null, coordinator_role: limpio(s.coordinator_role), observers: limpio(s.observers),
    participants_count: s.participants_count, expected_occupants: s.expected_occupants, assembly_count: s.assembly_count,
    alarm_time: s.alarm_time || null, evacuation_start_time: s.evacuation_start_time || null, last_arrival_time: s.last_arrival_time || null,
    incidents_notes: limpio(s.incidents_notes), lessons_learned: limpio(s.lessons_learned), conclusion: limpio(s.conclusion),
  };
}

export const tituloEvento = (s: Pick<FilaSimulacro, 'drill_type' | 'drill_type_other'>) =>
  `Simulacro: ${s.drill_type === 'otro' && s.drill_type_other?.trim() ? s.drill_type_other.trim() : ETIQUETA_TIPO[s.drill_type]}`;

// Programar: crea el simulacro y su evento del Calendario SSO (el recordatorio lo manda el cron de siempre a los
// eventos pendientes). Si el evento no se puede crear, el simulacro queda guardado y se avisa.
export async function programarSimulacro(companyId: string, planId: string, s: SimulacroEditable, usuarioId: string | null): Promise<{ fila: FilaSimulacro; avisoCalendario: string | null }> {
  const { data, error } = await supabase.from('emergency_drills')
    .insert({ ...filaParaGuardar({ ...s, status: 'programado' }), company_id: companyId, plan_id: planId, created_by: usuarioId, updated_by: usuarioId })
    .select('*');
  if (error || !data?.length) falla(error ?? { message: MENSAJE_SIN_PERMISO }, 'programar el simulacro');
  const fila = mapSimulacro((data as any[])[0]);
  const { data: ev, error: e2 } = await supabase.from('sso_calendar_events').insert({
    company_id: companyId, title: tituloEvento(fila), event_type: 'simulacro', related_id: fila.id, related_table: 'emergency_drills',
    due_date: fila.scheduled_date, status: 'pendiente', assigned_to: null, notes: fila.scenario, completed_date: null, created_by: usuarioId,
  }).select('id');
  if (e2 || !ev?.length) {
    console.error('programarSimulacro: calendario', e2);
    return { fila, avisoCalendario: 'El simulacro quedó programado, pero no se pudo crear el evento del Calendario SSO.' };
  }
  const calendarId = (ev as { id: string }[])[0].id;
  const { error: e3 } = await supabase.from('emergency_drills').update({ calendar_event_id: calendarId }).eq('id', fila.id);
  if (e3) console.error('programarSimulacro: enlace al calendario', e3);
  return { fila: { ...fila, calendar_event_id: calendarId }, avisoCalendario: null };
}

// Guardar cambios (programación o resultado). Si pasa a realizado o cancelado, el evento del calendario se cierra.
export async function guardarSimulacro(s: SimulacroEditable & { id: string; calendar_event_id?: string | null }, usuarioId: string | null): Promise<FilaSimulacro> {
  const { data, error } = await supabase.from('emergency_drills').update({ ...filaParaGuardar(s), updated_by: usuarioId }).eq('id', s.id).select('*');
  if (error) falla(error, 'guardar el simulacro');
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO);
  const fila = mapSimulacro((data as any[])[0]);
  if (fila.calendar_event_id && fila.status !== 'programado') {
    const { error: e2 } = await supabase.from('sso_calendar_events').update(fila.status === 'ejecutado'
      ? { status: 'completado', completed_date: fila.performed_date }
      : { status: 'cancelado' }).eq('id', fila.calendar_event_id);
    if (e2) console.error('guardarSimulacro: calendario', e2);
  }
  return fila;
}

// Solo admin (RLS de M32) y sin informe firmado (trigger de M38).
export async function borrarSimulacro(s: Pick<FilaSimulacro, 'id' | 'calendar_event_id'>): Promise<void> {
  const { data, error } = await supabase.from('emergency_drills').delete().eq('id', s.id).select('id');
  if (error) falla(error, 'borrar el simulacro');
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO);
  if (s.calendar_event_id) {
    const { error: e2 } = await supabase.from('sso_calendar_events').update({ status: 'cancelado' }).eq('id', s.calendar_event_id);
    if (e2) console.error('borrarSimulacro: calendario', e2);
  }
}

// ── Brigadistas presentes ──

export async function cargarBrigadistasPresentes(drillId: string): Promise<string[]> {
  const { data, error } = await supabase.from('emergency_drill_participants').select('worker_id').eq('drill_id', drillId).eq('role', 'brigadista');
  if (error) falla(error, 'leer los participantes del simulacro');
  return (data ?? []).map((r: { worker_id: string }) => r.worker_id);
}

export async function guardarBrigadistasPresentes(companyId: string, drillId: string, antes: string[], ahora: string[]): Promise<void> {
  const quitar = antes.filter(w => !ahora.includes(w));
  const agregar = ahora.filter(w => !antes.includes(w));
  if (quitar.length) {
    const { error } = await supabase.from('emergency_drill_participants').delete().eq('drill_id', drillId).eq('role', 'brigadista').in('worker_id', quitar);
    if (error) falla(error, 'actualizar los brigadistas presentes');
  }
  if (agregar.length) {
    const { error } = await supabase.from('emergency_drill_participants')
      .upsert(agregar.map(w => ({ company_id: companyId, drill_id: drillId, worker_id: w, role: 'brigadista' })), { onConflict: 'drill_id,worker_id' });
    if (error) falla(error, 'actualizar los brigadistas presentes');
  }
}

// ── Evidencias ──

export interface FilaEvidenciaSimulacro {
  id: string; company_id: string; drill_id: string; storage_path: string; file_name: string; mime_type: TipoEvidencia;
  size_bytes: number; uploaded_by: string | null; created_at: string;
}

export const rutaSimulacro = (companyId: string, drillId: string, carpeta: 'evidencias' | 'informe', archivo: string, marca = Date.now()) =>
  `${companyId}/simulacros/${drillId}/${carpeta}/${marca}-${archivo}`;
export const rutaAnual = (companyId: string, year: number, version: number, marca = Date.now()) =>
  `${companyId}/simulacros/anual/${year}-v${version}-${marca}.pdf`;

export async function cargarEvidenciasSimulacros(companyId: string): Promise<FilaEvidenciaSimulacro[]> {
  const { data, error } = await supabase.from('emergency_drill_evidence').select('*').eq('company_id', companyId).order('created_at');
  if (error) falla(error, 'leer las evidencias de los simulacros');
  return (data ?? []).map((r: any) => ({ ...r, size_bytes: Number(r.size_bytes) }));
}

const subirArchivo = async (ruta: string, cuerpo: Blob, tipo: string, accion: string) => {
  const { error } = await supabase.storage.from(BUCKET_PLAN).upload(ruta, cuerpo, { contentType: tipo, upsert: false });
  if (error) {
    const sinPermiso = /row-level security|unauthorized|403/i.test(error.message ?? '');
    throw new Error(sinPermiso ? MENSAJE_SIN_PERMISO : `No se pudo ${accion}: ${error.message}.`);
  }
};

export async function subirEvidenciaSimulacro(s: Pick<FilaSimulacro, 'id' | 'company_id'>, archivo: File, usuarioId: string | null): Promise<FilaEvidenciaSimulacro> {
  const r = await revisarEvidencia(archivo);
  if ('error' in r) throw new Error(r.error);
  const nombre = nombreSeguro(archivo.name, r.tipo);
  const ruta = rutaSimulacro(s.company_id, s.id, 'evidencias', nombre);
  const cuerpo = archivo.type === r.tipo ? archivo : new File([archivo], nombre, { type: r.tipo });
  await subirArchivo(ruta, cuerpo, r.tipo, 'subir la evidencia');
  const { data, error } = await supabase.from('emergency_drill_evidence').insert({
    company_id: s.company_id, drill_id: s.id, storage_path: ruta, file_name: archivo.name.slice(0, 200) || nombre, mime_type: r.tipo,
    size_bytes: archivo.size, uploaded_by: usuarioId,
  }).select('*');
  if (error || !data?.length) {
    await supabase.storage.from(BUCKET_PLAN).remove([ruta]).catch(() => {});
    throw new Error(error ? mensajeErrorPE(error, 'registrar la evidencia') : MENSAJE_SIN_PERMISO);
  }
  return { ...(data[0] as FilaEvidenciaSimulacro), size_bytes: Number((data[0] as FilaEvidenciaSimulacro).size_bytes) };
}

export async function borrarEvidenciaSimulacro(e: Pick<FilaEvidenciaSimulacro, 'id' | 'storage_path'>): Promise<void> {
  const { data, error } = await supabase.from('emergency_drill_evidence').delete().eq('id', e.id).select('id');
  if (error) falla(error, 'borrar la evidencia');
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO);
  const { error: eArchivo } = await supabase.storage.from(BUCKET_PLAN).remove([e.storage_path]);
  if (eArchivo) console.error('borrarEvidenciaSimulacro: archivo', eArchivo);
}

// URL firmada de 120 s (evidencias, informe firmado e informe anual).
export async function urlArchivoSimulacro(ruta: string, descarga?: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET_PLAN).createSignedUrl(ruta, SEGUNDOS_URL_EVIDENCIA, descarga ? { download: descarga } : undefined);
  if (error || !data?.signedUrl) throw new Error(`No se pudo abrir el archivo: ${error?.message ?? 'sin enlace'}.`);
  return data.signedUrl;
}

// ── Firma del informe (K6) ──

export interface FirmaParaSubir {
  rol: RolFirma; nombre: string; cedula: string; firmadoEn: string; hash: string;
  png?: Blob | null;      // trazo (firma en pantalla)
  archivo?: File | null;  // papel escaneado o PDF FirmaEC
}

// Sube trazos o archivos firmados y el PDF del informe en la carpeta informe/ (congelada) y marca el simulacro como
// firmado. Desde aquí el trigger de M38 lo bloquea. Si la fila no se actualiza, intenta quitar lo subido.
export async function firmarInforme(s: FilaSimulacro, metodo: MetodoFirma, firmas: FirmaParaSubir[], pdf: Blob, hashInforme: string): Promise<FilaSimulacro> {
  const subidos: string[] = [];
  const marca = Date.now();
  try {
    const guardadas: FirmaInforme[] = [];
    for (const f of firmas) {
      const g: FirmaInforme = { rol: f.rol, nombre: f.nombre.trim(), cedula: f.cedula.trim(), firmado_en: f.firmadoEn, hash: f.hash, metodo };
      if (f.png) {
        const ruta = rutaSimulacro(s.company_id, s.id, 'informe', `firma_${f.rol}.png`, marca);
        await subirArchivo(ruta, f.png, 'image/png', 'subir la firma'); subidos.push(ruta); g.firma_path = ruta;
      }
      if (f.archivo) {
        const r = await revisarEvidencia(f.archivo);
        if ('error' in r) throw new Error(r.error);
        const ruta = rutaSimulacro(s.company_id, s.id, 'informe', `firmado_${f.rol}-${nombreSeguro(f.archivo.name, r.tipo)}`, marca);
        await subirArchivo(ruta, f.archivo.type === r.tipo ? f.archivo : new File([f.archivo], 'firmado', { type: r.tipo }), r.tipo, 'subir el documento firmado');
        subidos.push(ruta); g.archivo_path = ruta;
      }
      guardadas.push(g);
    }
    const pdfPath = rutaSimulacro(s.company_id, s.id, 'informe', 'informe.pdf', marca);
    await subirArchivo(pdfPath, pdf, 'application/pdf', 'subir el informe'); subidos.push(pdfPath);
    const { data, error } = await supabase.from('emergency_drills').update({
      report_signed_at: new Date().toISOString(), report_method: metodo, report_signatures: guardadas, report_pdf_path: pdfPath, report_hash: hashInforme,
    }).eq('id', s.id).select('*');
    if (error) falla(error, 'firmar el informe');
    if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO);
    return mapSimulacro((data as any[])[0]);
  } catch (e) {
    // La carpeta informe/ no deja borrar a nadie autenticado (M38): si algo falló, los archivos quedan sin fila.
    if (subidos.length) console.error('firmarInforme: archivos subidos sin firmar', subidos.length);
    throw e;
  }
}

export async function reabrirSimulacro(drillId: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('reabrir_simulacro', { p_drill: drillId, p_motivo: motivo });
  if (error) throw new Error(error.code === '42501' ? 'Solo el administrador de la empresa puede reabrir un simulacro firmado.' : mensajeErrorPE(error, 'reabrir el simulacro'));
}

// ── Informe anual ──

export interface FilaInformeAnual {
  id: string; company_id: string; plan_id: string | null; year: number; version: number; pdf_path: string; method: MetodoFirma;
  signatures: FirmaInforme[]; content_hash: string | null; summary: Record<string, unknown>; signed_at: string; created_by: string | null; created_at: string;
}

export async function cargarInformesAnuales(companyId: string): Promise<FilaInformeAnual[]> {
  const { data, error } = await supabase.from('emergency_drill_annual_reports').select('*').eq('company_id', companyId)
    .order('year', { ascending: false }).order('version', { ascending: false });
  if (error) falla(error, 'leer los informes anuales de simulacros');
  return (data ?? []) as FilaInformeAnual[];
}

export async function guardarInformeAnual(d: {
  companyId: string; planId: string | null; year: number; version: number; metodo: MetodoFirma; firmas: FirmaParaSubir[]; pdf: Blob;
  hash: string; resumen: Record<string, unknown>; usuarioId: string | null;
}): Promise<FilaInformeAnual> {
  const marca = Date.now();
  const firmas: FirmaInforme[] = [];
  for (const f of d.firmas) {
    const g: FirmaInforme = { rol: f.rol, nombre: f.nombre.trim(), cedula: f.cedula.trim(), firmado_en: f.firmadoEn, hash: f.hash, metodo: d.metodo };
    if (f.png) {
      const ruta = `${d.companyId}/simulacros/anual/${d.year}-v${d.version}-${marca}-firma_${f.rol}.png`;
      await subirArchivo(ruta, f.png, 'image/png', 'subir la firma'); g.firma_path = ruta;
    }
    if (f.archivo) {
      const r = await revisarEvidencia(f.archivo);
      if ('error' in r) throw new Error(r.error);
      const ruta = `${d.companyId}/simulacros/anual/${d.year}-v${d.version}-${marca}-firmado_${f.rol}-${nombreSeguro(f.archivo.name, r.tipo)}`;
      await subirArchivo(ruta, f.archivo.type === r.tipo ? f.archivo : new File([f.archivo], 'firmado', { type: r.tipo }), r.tipo, 'subir el documento firmado');
      g.archivo_path = ruta;
    }
    firmas.push(g);
  }
  const ruta = rutaAnual(d.companyId, d.year, d.version, marca);
  await subirArchivo(ruta, d.pdf, 'application/pdf', 'subir el informe anual');
  const { data, error } = await supabase.from('emergency_drill_annual_reports').insert({
    company_id: d.companyId, plan_id: d.planId, year: d.year, version: d.version, pdf_path: ruta, method: d.metodo, signatures: firmas,
    content_hash: d.hash, summary: d.resumen, created_by: d.usuarioId,
  }).select('*');
  if (error) falla(error, 'guardar el informe anual');
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO);
  return (data as FilaInformeAnual[])[0];
}

export const nombreArchivoInforme = (s: Pick<FilaSimulacro, 'performed_date' | 'drill_date'>) =>
  `Informe_simulacro_${fechaEc(s.performed_date ?? s.drill_date).replace(/\//g, '-')}.pdf`;
